import { useState, type FormEvent } from "react";
import { doctors, formDepartments } from "@/mocks/site";

const SUBMIT_URL = "https://readdy.ai/api/form/daohqscvvta8mhpmepp0";

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function AppointmentForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const inputClasses =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 font-body text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";
  const labelClasses = "mb-1.5 block font-label text-xs font-semibold text-foreground-700";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("phone_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("Thank you. Our team will contact you shortly to confirm your appointment.");
      form.reset();
      return;
    }
    formData.delete("phone_alt");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "Something went wrong.";

      if (response.ok && code === "OK") {
        setStatus("success");
        setMessage("Thank you. Our team will contact you shortly to confirm your appointment.");
        form.reset();
      } else {
        setStatus("error");
        setMessage(String(serverMessage));
      }
    } catch {
      setStatus("error");
      setMessage("We could not submit your request right now. Please call the clinic or try again.");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-accent-200 bg-accent-50 p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-background-50">
          <i className="ri-check-line text-2xl" aria-hidden="true"></i>
        </span>
        <h3 className="mt-5 font-heading text-xl font-semibold text-foreground-950">Request received</h3>
        <p className="mx-auto mt-3 max-w-md font-body text-sm leading-relaxed text-foreground-700">{message}</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 cursor-pointer rounded-md border border-accent-300 px-5 py-2.5 font-label text-sm font-semibold text-accent-800 transition-colors hover:bg-accent-100"
        >
          Book another appointment
        </button>
      </div>
    );
  }

  return (
    <form
      id="appointment-request-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelClasses} htmlFor="patient_name">
            Patient Name <span className="text-accent-600">*</span>
          </label>
          <input
            id="patient_name"
            name="patient_name"
            type="text"
            required
            placeholder="Full name"
            className={inputClasses}
          />
        </div>

        <div>
          <label className={labelClasses} htmlFor="phone">
            Phone Number <span className="text-accent-600">*</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            placeholder="Mobile number"
            className={inputClasses}
          />
        </div>

        <div>
          <label className={labelClasses} htmlFor="email">
            Email <span className="text-accent-600">*</span>
          </label>
          <input id="email" name="email" type="email" required placeholder="you@example.com" className={inputClasses} />
        </div>

        <div>
          <label className={labelClasses} htmlFor="preferred_date">
            Preferred Date
          </label>
          <input
            id="preferred_date"
            name="preferred_date"
            type="date"
            min={today}
            className={inputClasses}
          />
        </div>

        <div>
          <label className={labelClasses} htmlFor="preferred_time">
            Preferred Time
          </label>
          <input id="preferred_time" name="preferred_time" type="time" className={inputClasses} />
        </div>

        <div>
          <label className={labelClasses} htmlFor="department">
            Department / Specialty
          </label>
          <select id="department" name="department" defaultValue="" className={inputClasses}>
            <option value="" disabled>
              Select a department
            </option>
            {formDepartments.map((department) => (
              <option key={department} value={department}>
                {department}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClasses} htmlFor="doctor">
            Doctor
          </label>
          <select id="doctor" name="doctor" defaultValue="" className={inputClasses}>
            <option value="" disabled>
              Select a doctor
            </option>
            {doctors.map((doctor) => (
              <option key={doctor.name} value={doctor.name}>
                {doctor.name}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className={labelClasses} htmlFor="message">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={500}
            placeholder="Briefly describe your symptoms or reason for the visit"
            className={`${inputClasses} resize-none`}
          ></textarea>
          <p className="mt-1.5 font-body text-xs text-foreground-500">Maximum 500 characters.</p>
        </div>

        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-honeypot-field"
        />
      </div>

      {status === "error" ? (
        <p role="alert" className="mt-5 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 font-body text-sm text-accent-900">
          {message}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-body text-xs leading-relaxed text-foreground-500">
          By submitting this form you agree to be contacted about your appointment request.
        </p>
        <button
          type="submit"
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-calendar-check-line text-base" aria-hidden="true"></i>
          Request Appointment
        </button>
      </div>
    </form>
  );
}